import React, { useState } from 'react';
import { Form, Button } from 'antd';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../api/client';
import { API_LOGIN_URL } from '../utils';
import { setSessionToken } from '../utils/session';
import { apiErrorMessage, isApiCode } from '../utils/apiErrors';
import { InputPassword, InputText } from '../components/Input';
import { completeMfaLogin } from '../services/mfa';
import { signInDevice } from '../services/deviceSessions';
import TurnstileWidget, { turnstileEnabled } from '../components/common/TurnstileWidget';
import { BrandHero } from '../components/brand/Brand';
import LegalLinks from '../components/legal/LegalLinks';

const Login = (props) => {
	return (
		<div className='auth-page'>
			<BrandHero />
			<h1>Sign in</h1>
			<LoginWidget {...props} />
			<LegalLinks />
		</div>
	);
};

export default Login;

/**
 * Where a redirected sign-in may go back to: a same-origin path in this app,
 * and never one that would undo the sign-in (`/logout` signs straight back
 * out) or send the person round again (`/login`). Anything else answers null,
 * and the sign-in lands on Home.
 *
 * Same-origin is checked the way a browser will read the path, not the way
 * it looks: the WHATWG URL parser reads `\` as `/` and drops tab and
 * newline, so `/\evil.example` and `/<tab>/evil.example` resolve to another
 * host — history.pushState refuses them and react-router falls back to
 * `window.location.assign`, which leaves the site. So the path must start
 * with a single `/` (not `//`, not `/\`), carry no backslash, no control
 * character and no whitespace, no encoded slash or backslash before its
 * query, and resolve against this origin to this origin.
 */
const ENCODED_SEPARATOR = /%(2f|5c)/i;
// eslint-disable-next-line no-control-regex
const CONTROL_OR_SPACE = /[\u0000-\u001F\u007F-\u009F\s]/;

export function usableReturnPath(raw) {
	if (typeof raw !== 'string' || !raw.startsWith('/')) return null;
	if (raw.startsWith('//') || raw.startsWith('/\\')) return null;
	if (raw.includes('\\') || CONTROL_OR_SPACE.test(raw)) return null;
	const pathPart = raw.split(/[?#]/)[0];
	if (ENCODED_SEPARATOR.test(pathPart)) return null;
	const origin = window.location.origin;
	let resolved;
	try {
		resolved = new URL(raw, origin);
	} catch {
		return null;
	}
	if (resolved.origin !== origin) return null;
	const pathname = pathPart.replace(/\/+$/, '') || '/';
	if (pathname === '/logout' || pathname === '/login') return null;
	return raw;
}

export function LoginWidget() {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();

	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [errorText, setErrorText] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [turnstileToken, setTurnstileToken] = useState(null);
	const [turnstileReset, setTurnstileReset] = useState(0);
	// The bot check is drawn only once the API asks for it (400
	// `challenge_required`, after repeated failures), then kept for the rest
	// of the page's life. A clean sign-in never sees it.
	const [challengeAsked, setChallengeAsked] = useState(false);
	// Set once the password has been accepted and a second factor is owed.
	const [challenge, setChallenge] = useState(null);
	const [code, setCode] = useState('');

	// Redirect info can arrive as router state (in-app navigation) or as
	// query params (the api client's 401 redirect).
	const isRedirect = location.state?.isRedirect || searchParams.get('isRedirect') === 'true';
	const askedReturnPath = location.state?.returnPath || searchParams.get('returnPath');
	const returnPath = usableReturnPath(askedReturnPath);

	// After a sign-in: back where the redirect came from; back one step for a
	// redirect that named nowhere; Home for a plain sign-in, or for a return
	// path that is not one to go back to.
	const land = () => {
		if (isRedirect && returnPath) navigate(returnPath);
		else if (isRedirect && !askedReturnPath) navigate(-1);
		else navigate('/');
	};

	const handleSubmit = () => {
		setSubmitting(true);
		setErrorText(null);
		api.post(API_LOGIN_URL, {
			username: (username + '').toLowerCase(),
			password,
			// The challenge token rides along once the widget is drawn and has
			// produced one; the API requires it only after repeated failures.
			...(turnstileToken ? { turnstileToken } : {}),
			// The phone app signs in on a device session (KPR-1674): a 1-hour
			// token and a refresh credential. A browser sends nothing and gets
			// the web's 4-week token, as ever.
			...signInDevice(),
		})
			.then(({ data }) => {
				// Two-step sign-in: a correct password answers with a
				// CHALLENGE, never a session. The second leg is what mints a
				// token, so there is nothing to store yet.
				if (data?.mfaRequired && data?.challenge) {
					setChallenge(data.challenge);
					return;
				}
				if (!data?.token) return;

				setSessionToken(data.token, { refreshToken: data.refreshToken || null });

				// A plain sign-in lands on Home — pinned launchers and records
				// from every collection. (Signup lands in the personal
				// collection instead: plan ruling d09, Register.js.)
				land();
			})
			.catch(error => {
				const status = error?.response?.status;
				if (isApiCode(error, 'challenge_required')) {
					setChallengeAsked(true);
					setErrorText(turnstileEnabled()
						? 'Complete the check below, then sign in again.'
						: apiErrorMessage(error));
				} else if (isApiCode(error, 'challenge_failed') || isApiCode(error, 'challenge_unavailable') || status === 429) {
					setErrorText(apiErrorMessage(error));
				} else if (status === 400 || status === 401) {
					// Bad password, unknown identifier, locked, blocked, suspended:
					// one answer, on purpose (docs/SCHEMA.md § Auth semantics).
					setErrorText('Incorrect email, username or password.');
				} else {
					setErrorText(apiErrorMessage(error, 'Could not sign in. Please try again.'));
				}
				// A challenge token is single-use: get a fresh one for the retry.
				setTurnstileReset(key => key + 1);
			})
			.finally(() => setSubmitting(false));
	};

	const validateForm = () => username.length > 0 && password.length > 0;

	// The second leg. A wrong code answers exactly as a wrong password does —
	// one refusal, no hint about which half was wrong.
	const submitCode = () => {
		setSubmitting(true);
		setErrorText(null);
		completeMfaLogin({ challenge, ...(code.includes('-') ? { recoveryCode: code } : { code }) })
			.then(data => {
				if (!data?.token) {
					setErrorText('That code is not right.');
					return;
				}
				setSessionToken(data.token, { refreshToken: data.refreshToken || null });
				land();
			})
			.catch(() => setErrorText('That code is not right.'))
			.finally(() => setSubmitting(false));
	};

	if (challenge) {
		return (
			<div className='login-container'>
				<Form className='m-1'>
					<p>Enter the code from your authenticator, or one of your recovery codes.</p>
					<InputText
						elementName='mfaCode'
						elementLabel='Code'
						value={code}
						autoComplete='one-time-code'
						onChange={(eleName, eleValue) => setCode(eleValue)}
					/>
					{errorText ? (
						<div role='alert' style={{ color: 'tomato', marginBottom: 8 }}>{errorText}</div>
					) : null}
					<Button type='primary' onClick={submitCode} disabled={!code} loading={submitting}>Sign in</Button>
				</Form>
			</div>
		);
	}

	return (
		<div className='login-container'>
			<Form className='m-1'>

				{/* One field for either identifier (KPR-547): an email address, or
				    the username a test or demo account was given. The API tells
				    them apart by the `@` and answers both the same way. An
				    identifier, so no autocorrect or capitals (platform-standards
				    § Text fields) — a phone that "corrects" demo.alice would
				    refuse a prospect for a typo they never made. */}
				<InputText
					elementName='username'
					elementLabel='Email or username'
					autoCapitalize={'none'}
					disableAutocorrect
					value={username}
					onChange={(eleName, eleValue) => setUsername(eleValue)}
					autoComplete='username'
				/>
				<InputPassword
					elementName='password'
					elementLabel='Password'
					value={password}
					onChange={(eleName, eleValue) => setPassword(eleValue)}
					autoComplete='current-password'
				/>

				{challengeAsked && turnstileEnabled() ? (
					<TurnstileWidget action='login' onToken={setTurnstileToken} resetKey={turnstileReset} />
				) : null}

				{errorText ? (
					<div role='alert' style={{ color: 'tomato', marginBottom: 8 }}>{errorText}</div>
				) : null}

				<Button type='primary' onClick={handleSubmit} disabled={!validateForm()} loading={submitting}>Sign in</Button>

			</Form>
			<div><Link className='hyperlink' to='/forgot-password'>Forgot password?</Link></div>
			Or <Link className='hyperlink' to='/register'>sign up</Link>
		</div>
	);
}
